import { HTTP_STATUS_CODE } from '@/constants/HttpStatusCode'
import { IAppState } from '@/interfaces/IAppState'
import { User } from '@/interfaces/IUser'
import router, { ROUTERS } from '@/router/router'
import * as Sentry from '@sentry/browser'
import axios from 'axios'
import Vue from 'vue'
import Vuex from 'vuex'
import {
	SHOW_SNACK,
	HIDE_SNACK,
	USER_SETDATA,
	APP_SET_MENU,
	LS_NAVIGATION_STATUS,
	APP_SET_NAVIGATION_STATUS,
	APP_SET_CONNECTION,
	APP_TOGGLE_LOADING_STATE,
	APP_SET_ERROR,
	APP_RESET_ERROR,
	USER_SET_RULES,
	SET_ROUTER_DATATABLE,
	SET_TOKEN,
	SET_LGPD_STATUS,
	SET_PARAMETROS_PROPOSTA,
	SET_NOTIFICATIONS,
	USER_ACCOUNTS_DATA
} from './MUTATIONS-TYPES'
import { INotificationsResponse } from '@/components/Notifications/NotificationItemBase'
import { NotificationHub } from '@/hubs/notification'
import { NotificationService } from '@/services/notification'
import amplitude from '@/services/AmplitudeService'
import { AttaServices } from '@/services/attaServices'
import { parseDateFromString } from '@/utils'
import { DateTime } from 'luxon'
import { ClosingServices } from '@/services/Closing'

Vue.use(Vuex)

export default new Vuex.Store<IAppState>({
	state: {
		user: null,
		notifications: new Array<INotificationsResponse>(),
		app: {
			isDataTableView: false,
			isLoading: false,
			isError: false,
			snackbar: {
				value: false,
				text: ''
			},
			error: {
				connection: false,
				message: '',
				exception: null
			},
			navigation: false,
			//@ts-ignore
			navigationExpand: JSON.parse(localStorage.getItem(LS_NAVIGATION_STATUS)) || false,
			tour: {},
			lgpdStatus: 0,
			parametros: null,
			isUserDespachante: false,
			lastCompletedClosingDate: DateTime.now().setZone('America/Sao_Paulo').minus({ months: 3 }),
			shouldUpdatePhone: false
		},
		userAccounts: new Array<User.IUserAccount>()
	},
	mutations: {
		[SHOW_SNACK](state, message) {
			state.app.snackbar.value = true
			state.app.snackbar.text = message
		},

		[HIDE_SNACK](state) {
			state.app.snackbar.value = false
		},

		[APP_SET_MENU](state, value) {
			state.app.navigation = value
		},

		[APP_SET_CONNECTION](state, payload: boolean) {
			state.app.error.connection = payload
		},

		[APP_SET_NAVIGATION_STATUS](state) {
			state.app.navigationExpand = !state.app.navigationExpand
			localStorage.setItem(LS_NAVIGATION_STATUS, state.app.navigationExpand.toString())
		},

		[USER_SETDATA](state, userPayLoad) {
			if (!userPayLoad) throw new Error('UserPayLoad is null!!!!!!!!!!!!')

			state.user = new User.User(userPayLoad)

			amplitude.setUserId(state.user.id?.toString())

			const identifyEvent = new amplitude.Identify()
			identifyEvent.set('parceiroId', state.user.parceiroId?.toString() || '')
			identifyEvent.set('franquiaId', state.user.franquiaId?.toString() || '')

			amplitude.identify(identifyEvent)
			amplitude.setGroup('role', state.user.role?.toString() || '')
		},

		[USER_ACCOUNTS_DATA](state, userAccountsPayLoad) {
			if (!userAccountsPayLoad) throw new Error('UserAccountsPayLoad is null!!!!!!!!!!!!')

			state.userAccounts = userAccountsPayLoad
		},

		[APP_TOGGLE_LOADING_STATE](state, value) {
			state.app.isLoading = value
		},

		[APP_SET_ERROR](state, { errorMessage, errorException, extras, silent }) {
			// If silent is true, the error will not be displayed
			if (!silent) {
				if (axios.isAxiosError(errorException) && errorException.response?.status === HTTP_STATUS_CODE.UNAUTHORIZED) {
					return
				}

				state.app.isError = true
				state.app.error.message = errorMessage
				state.app.error.exception = errorException
				state.app.error.payload = { extras }
			}

			// Sent to sentry
			Sentry.captureException(errorException, (scope) => {
				scope.setUser(state.user as any)
				scope.setTag('AppVersion', __VERSION_HASH__)
				scope.setTag('route', router.currentRoute.name)
				scope.setTag('path', router.currentRoute.fullPath)

				Sentry.setContext('App State', {
					routeName: router.currentRoute.name,
					routePath: router.currentRoute.fullPath,
					routeParams: JSON.stringify(router.currentRoute.params),
					appVersion: __VERSION_HASH__,
					store: JSON.stringify(state)
				})

				if (axios.isAxiosError(errorException)) {
					if (errorException.config) {
						const { method, url, baseURL } = errorException.config

						var buildRequestContext = {
							method: method,
							url: `${baseURL}/${url}`
						} as any

						console.log(errorException.toJSON())
						console.log(errorException.config.data)

						buildRequestContext = {
							...buildRequestContext,
							requestData: JSON.stringify(extras)
						}

						Sentry.setContext('request', buildRequestContext)
						Sentry.setExtra('request', buildRequestContext)
					}

					if (errorException.response) {
						const { status, data } = errorException.response

						scope.setTag('statusCode', status)
						scope.setExtra('Api Response', errorMessage)

						var buildResponseContext = {
							statusCode: status
						} as any

						if (data) {
							console.log(data)

							buildResponseContext = {
								...buildResponseContext,
								responseData: JSON.stringify(data)
							}
						}

						Sentry.setContext('response', buildResponseContext)
						Sentry.setExtra('response', buildResponseContext)

						Sentry.captureException(errorException)
					}
				}

				return scope
			})
		},

		[APP_RESET_ERROR](state) {
			state.app.isError = false
			state.app.error.message = ''
			state.app.error.exception = null
		},

		[USER_SET_RULES](state, rules) {
			if (state.user != null) state.user.rules = rules
		},
		[SET_ROUTER_DATATABLE](state, payload) {
			state.app.isDataTableView = payload
		},

		[SET_TOKEN](state, payload) {
			if (state.user) {
				state.user.accessToken = payload.access_token
				state.user.refreshToken = payload.refresh_token
			}
		},
		[SET_LGPD_STATUS](state, value) {
			state.app.lgpdStatus = value
		},
		[SET_PARAMETROS_PROPOSTA](state, params) {
			state.app.parametros = params
		},

		[SET_NOTIFICATIONS](state, notifications: Array<INotificationsResponse>) {
			state.notifications = notifications
		}
	},
	actions: {
		toggleLoadingState({ commit }, value) {
			commit(APP_TOGGLE_LOADING_STATE, value)
		},

		async initNotifications({ commit, state, dispatch }) {
			try {
				var hub = await NotificationHub.init()

				// Get notifications and set in store
				dispatch('getNotifications')

				hub.onNotificationReceived((notification) => {
					dispatch('getNotifications')
				})

				hub.onNotificationRead(() => {
					dispatch('getNotifications')
				})
			} catch (error) {
				console.log(error)
			}
		},

		async getUserDespachante({ state }) {
			if (state.user != null) {
				const result = await AttaServices.getFranchiseDespachante(state.user.franquiaId)
				state.app.isUserDespachante = result
			}
		},

		async getLastCompletedClosing({ state }) {
			if (state.user != null) {
				const result = await ClosingServices.getLastCompletedClosing()
				if (result?.result?.conciliationFinishedAt)
					state.app.lastCompletedClosingDate = parseDateFromString(result?.result?.conciliationFinishedAt)
			}
		},

		async getNotifications({ commit }) {
			try {
				const notifications = await NotificationService.get()
				commit(SET_NOTIFICATIONS, notifications.result)
			} catch (error) {
				console.log(error)
			}
		},

		async getShouldUserUpdatePhone({ state }) {
			if (state.user != null) {
				const result = await AttaServices.getShouldUserUpdatePhone()
				state.app.shouldUpdatePhone = result
			}
		},

		async setUserAccount({ state }) {
			const shouldSetUserAccount = state.userAccounts.length > 1 && !localStorage.getItem('ACCOUNT_SELECTED')
			if (shouldSetUserAccount) {
				router.push({ name: ROUTERS.ACCOUNT.SELECT_USER })
			}
		}
	},
	modules: {}
})
